import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Typography,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Step,
  Stepper,
  StepLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { apiService, Client, Bundle, BundleField, BundleAd } from '../services/api';

interface CreateAdModalProps {
  open: boolean;
  onClose: () => void;
  client: Client | null;
  adminSecret: string;
  onAdCreated: () => void;
}

const CreateAdModal: React.FC<CreateAdModalProps> = ({
  open,
  onClose,
  client,
  adminSecret,
  onAdCreated
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedBundle, setSelectedBundle] = useState<Bundle | null>(null);
  const [selectedBundleAd, setSelectedBundleAd] = useState<BundleAd | null>(null);
  const [matchId, setMatchId] = useState('');
  const [bundleFieldValues, setBundleFieldValues] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const steps = ['Select Bundle', 'Configure Ad'];

  useEffect(() => {
    if (open && client) {
      // Reset form when modal opens
      setActiveStep(0);
      setSelectedBundle(null);
      setSelectedBundleAd(null);
      setMatchId('');
      setBundleFieldValues({});
      setError('');
    }
  }, [open, client]);

  const handleBundleSelect = (event: SelectChangeEvent<string>) => {
    const bundleId = parseInt(event.target.value);
    const bundle = client?.bundles?.find(b => b.id === bundleId);
    if (bundle) {
      setSelectedBundle(bundle);
      setSelectedBundleAd(null);
      setBundleFieldValues({});
      // Move to next step
      setActiveStep(1);
    }
  };

  const handleBundleAdSelect = (event: SelectChangeEvent<string>) => {
    const adId = parseInt(event.target.value);
    const bundleAd = selectedBundle?.bundle_ads.find(ad => ad.id === adId);
    if (bundleAd) {
      setSelectedBundleAd(bundleAd);
    }
  };

  const handleFieldValueChange = (fieldName: string, value: string) => {
    setBundleFieldValues(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client || !selectedBundleAd || !matchId.trim()) return;

    setIsLoading(true);
    setError('');

    try {
      await apiService.createClientAd(
        client.tx_client_secret,
        {
          name: selectedBundleAd.tx_name,
          match_id: matchId.trim(),
          bundle_fields: bundleFieldValues,
          bundle_ad_id: selectedBundleAd.id
        }
      );
      
      onAdCreated();
      handleClose();
    } catch (error: any) {
      setError(error?.response?.data?.detail || 'Failed to create ad');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setActiveStep(0);
      setSelectedBundle(null);
      setSelectedBundleAd(null);
      setMatchId('');
      setBundleFieldValues({});
      setError('');
      onClose();
    }
  };

  const handleBack = () => {
    setActiveStep(0);
    setSelectedBundleAd(null);
    setBundleFieldValues({});
  };

  if (!client) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Create Ad for {client.tx_name}
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ pt: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Step 1: Select Bundle
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose which bundle you want to create an ad for.
              </Typography>

              <FormControl fullWidth>
                <InputLabel id="bundle-select-label">Select Bundle</InputLabel>
                <Select
                  labelId="bundle-select-label"
                  value={selectedBundle?.id?.toString() || ''}
                  onChange={handleBundleSelect}
                  label="Select Bundle"
                  disabled={isLoading}
                >
                  {client.bundles?.map((bundle) => (
                    <MenuItem key={bundle.id} value={bundle.id.toString()}>
                      {bundle.tx_name} (v{bundle.tx_version})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {(!client.bundles || client.bundles.length === 0) && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  This client has no associated bundles. Please associate bundles with this client first.
                </Alert>
              )}
            </Box>
          )}

          {activeStep === 1 && selectedBundle && (
            <Box component="form" onSubmit={handleSubmit}>
              <Typography variant="h6" gutterBottom>
                Step 2: Configure Ad
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Bundle: {selectedBundle.tx_name} (v{selectedBundle.tx_version})
              </Typography>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="bundle-ad-select-label">Select Ad Type</InputLabel>
                <Select
                  labelId="bundle-ad-select-label"
                  value={selectedBundleAd?.id?.toString() || ''}
                  onChange={handleBundleAdSelect}
                  label="Select Ad Type"
                  disabled={isLoading}
                >
                  {selectedBundle.bundle_ads.map((bundleAd) => (
                    <MenuItem key={bundleAd.id} value={bundleAd.id.toString()}>
                      {bundleAd.tx_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Match ID"
                value={matchId}
                onChange={(e) => setMatchId(e.target.value)}
                required
                disabled={isLoading}
                sx={{ mb: 2 }}
                helperText="Unique identifier for this ad"
              />

              {selectedBundle.bundle_fields.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Bundle Fields
                  </Typography>
                  {selectedBundle.bundle_fields.map((field) => (
                    <FormControl key={field.id} fullWidth sx={{ mb: 2 }}>
                      <InputLabel id={`field-${field.id}-label`}>
                        {field.tx_client_field_name || field.tx_name}
                      </InputLabel>
                      <Select
                        labelId={`field-${field.id}-label`}
                        value={bundleFieldValues[field.tx_name] || ''}
                        onChange={(e) => handleFieldValueChange(field.tx_name, e.target.value)}
                        label={field.tx_client_field_name || field.tx_name}
                        disabled={isLoading}
                      >
                        <MenuItem value="">
                          <em>Choose an option...</em>
                        </MenuItem>
                        {field.ar_options?.map((option, index) => (
                          <MenuItem key={index} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                      {field.tx_description && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          {field.tx_description}
                        </Typography>
                      )}
                    </FormControl>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        {activeStep === 1 && (
          <Button onClick={handleBack} disabled={isLoading}>
            Back
          </Button>
        )}
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        {activeStep === 1 && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isLoading || !selectedBundleAd || !matchId.trim()}
          >
            {isLoading ? 'Creating...' : 'Create Ad'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateAdModal;